import React, { useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

import { fromLegacyTimeZone, getUTCOffsetWithoutDST } from '@bingads-webui-universal/time-zone-converter';
import { translateLegacyToIANATimezone } from '@bingads-webui-universal/temporal-utitlities';

import { AssetLibraryAppNameODataHeader, getLogVersion, getDefaultMetricDateRange } from '@bingads-webui-campaign-react/asset-library-model';

import { getLoggerWithoutPageInfo } from './logger';
import { useAssetLibraryUserPreference, useAssetLibraryAccountPreference } from './preferences';
import {
  createFolderRepository,
  createImageRepository,
  createVideoRepository,
  createAIGCRepository,
} from './repository-creation-utility';
import {
  AssetWithFoldersDataServiceProvider,
  withAssetLibraryServicesConsumer
} from './data-service';


export function withAssetLibraryServicesProvider(WrappedComponent, { loggerTag } = {}) {
  const Wrapper = (props) => {
    const {
      aid,
      cid,
      accountTimeZone,
      oDataConfig,
      preferenceService,
      activity,
      i18n,
      allowedAssetType,
      permissions,
      perfMetricDateRange,
      dateFormat,
    } = props;

    const tz = fromLegacyTimeZone(accountTimeZone);
    const timezoneUTCOffset = getUTCOffsetWithoutDST(tz);
    const ianaTimeZone = translateLegacyToIANATimezone(accountTimeZone);

    // get preference service
    const assetLibraryUserPreferences = useAssetLibraryUserPreference({ preferenceService });
    const assetLibraryAccountPreferences = useAssetLibraryAccountPreference({ preferenceService });

    const versionForLog = useMemo(() => (getLogVersion(permissions)), [permissions]);
    const logger = useMemo(() => getLoggerWithoutPageInfo(activity, versionForLog, loggerTag), [activity, versionForLog]);

    const oDataConfigObj = useMemo(() => ({
      odataToken: oDataConfig.odataToken || oDataConfig.oDataToken,
      oDataToken: oDataConfig.odataToken || oDataConfig.oDataToken,
      odataPath: oDataConfig.odataPath || oDataConfig.oDataPath,
      oDataPath: oDataConfig.odataPath || oDataConfig.oDataPath,
      odataTokenType: oDataConfig.oDataTokenType || oDataConfig.odataTokenType,
      oDataTokenType: oDataConfig.oDataTokenType || oDataConfig.odataTokenType,
      applicationName: AssetLibraryAppNameODataHeader.value,
      beforeSend: xhr => ({
        onBeforeSendDone: () => {
          xhr.setRequestHeader(AssetLibraryAppNameODataHeader.key, AssetLibraryAppNameODataHeader.value);
        },
      }),
    }), [oDataConfig]);

    const imageRepository = useMemo(
      () => createImageRepository({
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        logger,
        i18n,
        ianaTimeZone,
        timezoneUTCOffset,
        activity,
      }),
      [oDataConfigObj, aid, cid, logger, i18n, ianaTimeZone, timezoneUTCOffset, activity]
    );

    const folderRepository = useMemo(
      () => createFolderRepository({
        i18n,
        ianaTimeZone,
        timezoneUTCOffset,
        logger,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        dateFormat,
      }),
      [logger, i18n, ianaTimeZone, timezoneUTCOffset, oDataConfigObj, aid, cid, dateFormat]
    );

    const aigcRepository = useMemo(
      () => createAIGCRepository({ oDataConfig: oDataConfigObj, aid, cid }),
      [aid, cid, oDataConfigObj]
    );

    const videoRepository = useMemo(
      () => createVideoRepository({
        oDataConfig: oDataConfigObj, aid, cid,
      }),
      [aid, cid, oDataConfigObj]
    );

    const defaultPerfMetricDateRange = useMemo(() => getDefaultMetricDateRange(
      i18n, dateFormat,
      ianaTimeZone,
      perfMetricDateRange && perfMetricDateRange.Rangelcid
    ), [dateFormat, i18n, ianaTimeZone, perfMetricDateRange]);

    return (
      <AssetWithFoldersDataServiceProvider
        imageRepository={imageRepository}
        folderRepository={folderRepository}
        aigcRepository={aigcRepository}
        videoRepository={videoRepository}
        logger={logger}
        allowedAssetType={allowedAssetType}
        i18n={i18n}
        perfMetricDateRange={perfMetricDateRange}
        defaultPerfMetricDateRange={defaultPerfMetricDateRange}
      >
        <WrappedComponent
          ianaTimeZone={ianaTimeZone}
          assetLibraryUserPreferences={assetLibraryUserPreferences}
          assetLibraryAccountPreferences={assetLibraryAccountPreferences}
          logger={logger}
          {...props}
        />
      </AssetWithFoldersDataServiceProvider>
    );
  };

  Wrapper.displayName = `withAssetLibraryServicesProvider(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
}

export function withAssetLibraryServicesLoaded(WrappedComponent, options) {
  const Wrapper = withAssetLibraryServicesProvider(withAssetLibraryServicesConsumer(WrappedComponent), options);

  Wrapper.displayName = `withAssetLibraryServicesLoaded(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
}
